import { useState } from 'react'

import { lightAgeYears, ru, СВЕТОВОЙ_ГОД_М } from '../../lib/physics'
import { Predict } from './common'

/**
 * Свет из прошлого (урок 9): свет идёт с конечной скоростью, 3·10⁸ м/с. Глядя на далёкий
 * объект, мы видим его таким, каким он был, когда свет вышел. Чем дальше — тем древнее картинка;
 * что там сейчас, узнать нельзя. Отсюда и оценка размера Вселенной: не больше c·t.
 */

const ОБЪЕКТЫ = [
  { что: 'Луна', d: 3.84e8 },
  { что: 'Солнце', d: 1.5e11 },
  { что: 'Сатурн', d: 1.4e12 },
  { что: 'ближайшая звезда', d: 4.2 * СВЕТОВОЙ_ГОД_М },
  { что: 'центр Галактики', d: 2.6e4 * СВЕТОВОЙ_ГОД_М },
  { что: 'галактика Андромеды', d: 2.5e6 * СВЕТОВОЙ_ГОД_М },
  { что: 'далёкий квазар', d: 1e9 * СВЕТОВОЙ_ГОД_М },
] as const

/** Возраст света — удобными единицами: секунды, минуты, часы, годы. */
function возраст(годы: number): string {
  const с = годы * 365 * 86400
  if (с < 60) return `${ru(с, 1)} с`
  if (с < 3600) return `${ru(с / 60, 1)} мин`
  if (с < 86400 * 2) return `${ru(с / 3600, 1)} ч`
  if (годы < 1e3) return `${ru(годы, 1)} года`
  if (годы < 1e6) return `${ru(годы / 1e3, 0)} тысяч лет`
  if (годы < 1e9) return `${ru(годы / 1e6, 1)} млн лет`
  return `${ru(годы / 1e9, 1)} млрд лет`
}

export default function LightAgeLab() {
  const [номер, setНомер] = useState(1)
  const о = ОБЪЕКТЫ[номер]
  const годы = lightAgeYears(о.d)
  // положение на рисунке — по логарифму расстояния
  const x = 40 + ((Math.log10(о.d) - 8) / (25 - 8)) * 290

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 100" role="img" aria-label="Свет идёт от объекта к Земле">
        <circle cx={22} cy={50} r={12} className="fill-earth ink" strokeWidth={1} />
        <text x={22} y={82} textAnchor="middle" className="lab-tick">Земля</text>
        <line x1={36} y1={50} x2={x - 10} y2={50} className="ink-el" strokeWidth={2} strokeDasharray="6 4" />
        <polygon points="40,50 50,45 50,55" className="fill-el" />
        <circle cx={x} cy={50} r={9} className="fill-heavy" />
        <text x={Math.min(x, 300)} y={30} textAnchor="middle" className="lab-label heavy">{о.что}</text>
      </svg>

      <div className="lab-big">мы видим, каким это было <b>{возраст(годы)}</b> назад</div>
      <div className="lab-readout">
        <span>расстояние {о.d < 1e15 ? `${ru(о.d / 1e9, 2)} млн км` : `${ru(о.d / СВЕТОВОЙ_ГОД_М, 1)} св. лет`}</span>
        <span>t = d / c, c = 3·10⁸ м/с</span>
      </div>

      <label className="lab-slider">
        <span>Куда смотрим</span>
        <input type="range" min={0} max={ОБЪЕКТЫ.length - 1} step={1} value={номер} onChange={(e) => setНомер(Number(e.target.value))} />
      </label>

      <Predict
        question="Солнце — в 150 млн км. Если бы оно погасло, когда мы бы это заметили?"
        options={['сразу', 'через 8 минут', 'через год']}
        right={1}
        revealed={о.что === 'Солнце'}
        explain="t = 1,5·10¹¹ м : 3·10⁸ м/с = 500 с ≈ 8 минут. Всё это время мы видели бы свет, вышедший раньше."
      />
      <p className="muted small">
        Световой год — путь света за год, около 9,5·10¹⁵ м. Вселенной около 14 млрд лет, поэтому её размер
        не больше c·t ≈ 10²⁶ м. Это оценка: важен порядок, а не точное число.
      </p>
    </div>
  )
}
